import { createFileRoute } from '@tanstack/react-router'
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Stack,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    alpha
} from '@mui/material'
import { useSEO, useBreadcrumbSchema, PageSEO } from '../../lib/seo'
import LinkOffIcon from '@mui/icons-material/LinkOff';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SecurityIcon from '@mui/icons-material/Security';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { PricingCard } from '../../components/ui/PricingCard'
import { BrandBackdrop } from '../../components/brand/BrandBackdrop'
import { marketing } from '../../styles/theme'
import { PricingContentLayout } from '../../components/sections/PricingContentLayout'
import { Section } from '../../components/ui/Section'
import { SectionHeader } from '../../components/ui/SectionHeader'
import { FadeIn } from '../../components/ui/FadeIn'
import { LogoMarqueeSection } from '../../components/sections/LogoMarqueeSection'
import { TestimonialsSection } from '../../components/sections/TestimonialsSection'
import { InfoCard } from '../../components/ui/InfoCard'
import { FeatureIcon } from '../../components/ui/FeatureIcon'
import { DarkCalloutSection } from '../../components/ui/DarkCalloutSection'
import { BrandColors } from '@neumind/shared/branding'
import { useCurrency } from '../../contexts/CurrencyContext'
import { PricingTiers } from '../../lib/pricing-data'
import { formatPrice } from '../../lib/pricing-utils'
import { ExternalUrls } from '../../lib/constants'

const HOURLY_RATE = { GBP: '£120', USD: '$150', EUR: '€140', AUD: '$230', INR: '₹5,000' };
const STANDARD_APP_COST = { GBP: '£15/mo', USD: '$19/mo', EUR: '€18/mo', AUD: '$29/mo', INR: '₹499/mo' };

export const Route = createFileRoute('/solutions/consulting')({
    component: RouteComponent,
})

function RouteComponent() {
    useSEO(PageSEO.consulting);
    useBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Solutions', url: '/solutions' },
        { name: 'Consulting', url: '/solutions/consulting' }
    ]);

    const { currency } = useCurrency();
    return (
        <Box>
            {/* Hero Section */}
            <Box sx={{
                pt: { xs: 12, md: 16 },
                pb: { xs: 8, md: 12 },
                background: 'linear-gradient(135deg, rgba(77, 101, 255, 0.05) 0%, rgba(14, 165, 223, 0.05) 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Brand-atmosphere layer: cool mesh wash + Care Circle constellation
                    (signature motif) fading from the top-right shoulder. Subtle per
                    the visual-language brief — blue ≤10%, cyan ≤6%. Mirrors the
                    homepage hero. */}
                <BrandBackdrop
                    mesh="light"
                    constellation
                    constellationCorner="top-right"
                    constellationOpacity={0.6}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <FadeIn direction="up">
                                <Typography variant="h1" gutterBottom>
                                    Assistive-Tech Implementation for{' '}
                                    <Box component="span" sx={{ color: 'primary.main' }}>Brain-Injury Rehabilitation</Box>
                                </Typography>
                                <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                                    We configure the device, translate OT goals into prompts, and document outcomes for Deputies and Case Managers—expert implementation for <strong>any assistive technology</strong>, from bespoke smart-home setups to Neumind app configuration.
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        href={ExternalUrls.calendly}
                                        target="_blank"
                                        sx={{ px: 4, py: 1.5 }}
                                    >
                                        Book Free Consultation
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        href="/contact-us"
                                        sx={{ px: 4, py: 1.5 }}
                                    >
                                        Get in touch
                                    </Button>
                                </Stack>

                                {/* Low-commitment CTA: links to the static sample-report
                                    gallery (one client, five audience-tailored variants).
                                    Hosted on www per the canon decision — no share.neumind.io
                                    dependency. */}
                                <Typography variant="body2" color="text.secondary">
                                    Or{' '}
                                    <Link
                                        href="/sample-reports/sarah/index.html"
                                        target="_blank"
                                        rel="noopener"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        see a sample outcomes report
                                    </Link>
                                    {' '}— the documentation we produce for Deputies and Case Managers.
                                </Typography>

                            </FadeIn>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FadeIn direction="left" delay={200}>
                                {/* Frame the real product visual forward: orb-halo
                                    brand motif glows behind the image, with a
                                    navy-tinted elevation shadow on the frame — depth
                                    without inventing a fake card. Mirrors the
                                    homepage hero treatment. */}
                                <Box sx={{ position: 'relative' }}>
                                    <BrandBackdrop
                                        motif="orb-halo"
                                        motifCorner="top-right"
                                        motifColor="37,99,235"
                                        motifOpacity={0.16}
                                        motifSize={460}
                                    />
                                    <Box
                                        component="img"
                                        src="/images/hero/hero-tech-consulting.webp"
                                        alt="Clinician using Neumind dashboard on tablet with client"
                                        fetchPriority="high"
                                        loading="eager"
                                        decoding="async"
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: 4,
                                            boxShadow: marketing.shadows.hero,
                                        }}
                                    />
                                </Box>
                            </FadeIn>
                        </Grid>
                    </Grid>

                </Container>
            </Box>

            {/* Social Proof */}
            <LogoMarqueeSection tagline="Clinical teams we work with" relations={['customer-user']} speed={40} />

            {/* The Why (Problem/Solution) */}
            <Section>
                <Container maxWidth="lg">
                    <SectionHeader
                        overline="The Challenge"
                        title="Why Implementation Matters"
                        subtitle="Great technology fails without proper setup. We bridge the gap between purchase and outcomes."
                        centered
                        titleVariant="h2"
                    />
                    <Grid container spacing={4}>
                        {[
                            {
                                icon: <LinkOffIcon fontSize="large" />,
                                title: "The Implementation Gap",
                                description: "Patients with executive dysfunction need structure the most but struggle to set it up. Clinicians often lack the technical time to configure complex digital ecosystems.",
                                color: BrandColors.error.main
                            },
                            {
                                icon: <HandshakeIcon fontSize="large" />,
                                title: "Done-With-You Implementation",
                                description: "We don't just send you a login. Our specialists configure the device, translate rehab goals into prompts, and train the user using errorless learning principles.",
                                color: BrandColors.success.main
                            },
                            {
                                icon: <SecurityIcon fontSize="large" />,
                                title: "Defensible Outcomes",
                                description: "Higher adherence, reduced abandonment, and documentation to satisfy Deputies and Case Managers.",
                                color: BrandColors.primary.main
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
                                <FadeIn delay={index * 100} direction="up" style={{ width: '100%' }}>
                                    <InfoCard
                                        title={item.title}
                                        description={item.description}
                                        icon={<FeatureIcon icon={item.icon} color={item.color} />}
                                    />
                                </FadeIn>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Section>

            {/* Primary Offering - Neumind Access */}
            <Section variant="gradient">
                <Container maxWidth="md">
                    <SectionHeader
                        overline="Pricing"
                        title="Investment Options"
                        subtitle="Choose the level of support that fits your needs."
                        centered
                        titleVariant="h2"
                        sx={{ mb: 4 }}
                    />
                    <FadeIn direction="up">
                        <PricingContentLayout maxWidth={12}>
                            <Grid item xs={12} md={6}>
                                <PricingCard
                                    title="Hourly & Ad-Hoc"
                                    price={HOURLY_RATE[currency]}
                                    priceNote="per hour"
                                    description="Flexible expert support for bespoke tech implementation."
                                    highlighted={false}
                                    ctaText="Enquire Now"
                                    ctaHref="/contact-us"
                                    features={[
                                        "Bespoke Smart Home Setup",
                                        "Non-Neumind App Configuration",
                                        "Expert Witness Reports",
                                        "Complex Needs Assessment",
                                        "Clinic Staff Training"
                                    ]}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PricingCard
                                    title="Neumind Access"
                                    price={formatPrice(PricingTiers.recoveryArc.integrationAddOn[currency], currency)}
                                    priceNote="one-off fee"
                                    description="The complete 'Concept-to-Completion' service for new referrals."
                                    highlighted={true}
                                    ctaText="Book Free Consultation"
                                    ctaHref={ExternalUrls.calendly}
                                    features={[
                                        "Digital Clinical Intake & Deficit Analysis",
                                        "90-Minute Specialist Setup & Training Session",
                                        "Clinical Translation of OT Goals to Prompts",
                                        "30 Days Priority Support for Teething Issues"
                                    ]}
                                />
                            </Grid>
                        </PricingContentLayout>
                    </FadeIn>
                </Container>
            </Section>

            {/* Bespoke Consulting */}
            <DarkCalloutSection
                title="Complex Needs & Clinic-Wide Implementation"
                subtitle="For multi-disciplinary teams, complex smart-home integrations, or whole-clinic training, we offer bespoke consulting services."
                items={['Clinic Staff Training', 'Smart Home/IoT Integration', 'Ongoing Quarterly Reviews']}
                ctaLabel="Contact Us for a Quote"
                ctaHref="/contact-us"
            />

            {/* Comparison Table */}
            <Section>
                <Container maxWidth="lg">
                    <SectionHeader
                        title="Why Choose a Clinical Approach?"
                        titleVariant="h2"
                        centered
                        sx={{ mb: 6 }}
                    />
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                        <Table size="small" sx={{ '& th, & td': { px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell component="th" scope="col"><Typography variant="subtitle2" fontWeight={700} fontSize="inherit">Feature</Typography></TableCell>
                                    <TableCell component="th" scope="col" align="center"><Typography variant="subtitle2" color="text.secondary" fontSize="inherit">General IT Support</Typography></TableCell>
                                    <TableCell component="th" scope="col" align="center"><Typography variant="subtitle2" color="text.secondary" fontSize="inherit">Standard App Subscription</Typography></TableCell>
                                    <TableCell component="th" scope="col" align="center"><Typography variant="subtitle2" color="primary.main" fontWeight={700} fontSize="inherit">Neumind Access</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { feature: "Clinical Understanding", it: false, app: false, neu: true, neuNote: "Neuro-specialist" },
                                    { feature: "Goal-to-Feature Mapping", it: false, app: false, neu: true },
                                    { feature: "Justification Report", it: false, app: false, neu: true },
                                    { feature: "User Training", it: false, app: "Self-Service", neu: true, neuNote: "Errorless Learning" },
                                    { feature: "Cost", it: "£60-100/hr", app: STANDARD_APP_COST[currency], neuText: `${formatPrice(PricingTiers.recoveryArc.integrationAddOn[currency], currency)} Fixed` },
                                ].map((row) => (
                                    <TableRow key={row.feature}>
                                        <TableCell component="th" scope="row"><Typography variant="body2" fontWeight={600} fontSize="inherit">{row.feature}</Typography></TableCell>
                                        <TableCell align="center">
                                            {row.it === false ? <CloseIcon color="error" fontSize="small" /> : <Typography variant="body2" fontSize="inherit">{row.it}</Typography>}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.app === false ? <CloseIcon color="error" fontSize="small" /> : <Typography variant="body2" fontSize="inherit">{row.app}</Typography>}
                                        </TableCell>
                                        <TableCell align="center" sx={{ bgcolor: alpha(BrandColors.primary.main, 0.05) }}>
                                            {row.neuText ? (
                                                <Typography variant="body2" fontWeight={700} color="primary.main" fontSize="inherit">{row.neuText}</Typography>
                                            ) : (
                                                <Stack alignItems="center" spacing={0.5}>
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                    {row.neuNote && (
                                                        <Typography variant="caption" color="text.secondary" fontSize="inherit">{row.neuNote}</Typography>
                                                    )}
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Container>
            </Section>

            {/* Testimonials */}
            <TestimonialsSection
                title="What clinical teams say"
                subtitle="Feedback from clinicians using neumind with their clients."
                userType={['professional']}
                background="paper"
            />

            {/* How We Work */}
            <Section variant="dark" py={8}>
                <Container maxWidth="md">
                    <SectionHeader
                        title="How We Work"
                        titleVariant="h3"
                        variant="dark"
                        subtitle={
                            <>
                                We start with a clinical intake and deficit analysis, then translate each rehab goal into a configured prompt and train the user with errorless-learning principles. Every engagement closes with a justification report that documents the setup and intended outcomes—the evidence Deputies and Case Managers need to defend the spend.
                            </>
                        }
                        centered
                        sx={{ mb: 0 }}
                    />
                </Container>
            </Section>
        </Box >
    )
}
